/**
 * Datenanbindung fuer die Oberflaeche.
 *
 * Bewusst ohne Abfragebibliothek: die Anwendung liest, blaettert und filtert
 * — mehr braucht es nicht, und jede Bibliothek waere ein weiteres Paket im
 * Auslieferungspaket.
 *
 * Zwei Dinge, die sonst gern schiefgehen, sind hier geregelt:
 *   - Jede neue Anfrage bricht die vorherige ab. Wer tippt, loest zehn
 *     Suchen aus; ohne Abbruch gewinnt zufaellig die langsamste.
 *   - Der Ladezustand blinkt nicht. Kommen Daten in unter 220 ms, wird gar
 *     kein Platzhalter gezeigt.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { alsAbfrage, holen, standLesen, type Seite } from './klient.js';
import { tokenHolen } from '../anmeldung/msal.js';

export interface Zustand<T> {
  daten: T | null;
  laedt: boolean;
  fehler: string | null;
  erneut: () => void;
}

const BLINKSCHUTZ = 220;

/**
 * Meldung, dass sich am Bestand etwas geändert hat.
 *
 * Bis zum 13.08.2026 brauchte es sie nicht: es gab keinen Weg, etwas
 * anzulegen. Mit dem Anlegepfad entsteht die Frage, wie eine Liste erfährt,
 * dass hinter ihr geschrieben wurde — der Drawer hängt über der ganzen
 * Anwendung und kennt die Haken der einzelnen Ansichten nicht.
 *
 * Bewusst ein Ereignis am Fenster und kein Zustandsspeicher: die Nachricht
 * ist flüchtig, hat keinen Inhalt außer dem Namen der Sammlung, und niemand
 * muss sie aufbewahren. Ein Speicher dafür wäre ein zweiter Ort, an dem
 * Wahrheit über den Bestand steht.
 */
export const BESTAND_GEAENDERT = 'iammatrix:bestand-geaendert';

/** Was an einer Meldung `bestandGeaendert` haengt. */
export interface Bestandsmeldung {
  sammlung: string;
  /**
   * Ohne Ladeanzeige nachladen.
   *
   * Gesetzt von der Live-Abfrage (`api/live.ts`): eine Liste, die sich alle
   * fuenfzehn Sekunden mit einem Ladebalken meldet, weil **jemand anders**
   * etwas geschrieben hat, flackert ohne Anlass fuer den, der davor sitzt.
   * Ein stiller Abruf, der scheitert, laesst die alten Zeilen stehen.
   */
  still?: boolean;
  /**
   * Die Meldung kam von aussen — aus einem anderen Reiter oder von der
   * Live-Abfrage — und wird nicht noch einmal an die anderen Reiter
   * weitergereicht.
   */
  fremd?: boolean;
}

/** Meldet, dass in einer Sammlung geschrieben wurde. */
export function bestandGeaendert(
  sammlung: string,
  optionen: { still?: boolean; fremd?: boolean } = {},
): void {
  const detail: Bestandsmeldung = { sammlung, ...optionen };
  window.dispatchEvent(new CustomEvent(BESTAND_GEAENDERT, { detail }));
}

/**
 * Ein Abruf gegen die Schnittstelle — die Grundlage aller Haken hier.
 *
 * Ausgefuehrt, weil nicht jeder Endpunkt eine Sammlung ist. `/api/sod/regeln`
 * und `/api/sod/befunde` antworten in derselben Form wie eine Liste, nehmen
 * aber keine Blaetter- und Sortierparameter entgegen. Sie ueber `useListe` zu
 * holen haengte `?seite=1&groesse=200` an einen Pfad, der das nicht kennt —
 * genau die Konstruktion, die am 13.08.2026 als `_stand`-Filter mit 400
 * abgewiesen wurde und im Entwicklungsbetrieb still eine leere Liste ergab.
 *
 * `pfad` ist der vollstaendige Pfad **mit** `/api`. `null` schaltet den Abruf
 * ab; dann geht nichts hinaus.
 */
export function useAbruf<T>(
  pfad: string | null,
  mandant: string,
  /**
   * Wie geladen wird — Vorgabe ist ein einzelner `holen`. `useAlleSeiten`
   * setzt hier einen Lader ein, der Seite um Seite holt. Er steht bewusst
   * **nicht** in den Abhaengigkeiten: der Pfad bestimmt, was geladen wird.
   */
  laden?: (pfad: string, optionen: { mandant: string; token: string | undefined; signal: AbortSignal }) => Promise<T>,
): Zustand<T> {
  /* Der Stichtag eines Rueckblicks. Gelesen wird er bei jedem Zeichnen —
     gesetzt wird er im Standrahmen, und der zeichnet die Anwendung darunter
     neu, sobald er sich aendert. */
  const stichtag = standLesen();
  const [daten, setDaten] = useState<T | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const laufend = useRef<AbortController | null>(null);
  /* Der naechste Abruf ist ein stiller (siehe `Bestandsmeldung.still`). */
  const stillNaechster = useRef(false);

  useEffect(() => {
    if (!pfad) return;
    laufend.current?.abort();
    const steuerung = new AbortController();
    laufend.current = steuerung;

    const still = stillNaechster.current;
    stillNaechster.current = false;
    let blinkschutz: number | undefined = still
      ? undefined
      : window.setTimeout(() => setLaedt(true), BLINKSCHUTZ);
    if (!still) setFehler(null);

    void (async () => {
      try {
        const token = await tokenHolen();
        const ergebnis = laden
          ? await laden(pfad, { mandant, token, signal: steuerung.signal })
          : await holen<T>(pfad, { mandant, token, signal: steuerung.signal });
        if (steuerung.signal.aborted) return;
        setDaten(ergebnis);
      } catch (f) {
        if (steuerung.signal.aborted) return;
        if (f instanceof DOMException && f.name === 'AbortError') return;
        /* Ein stiller Abruf, der scheitert, laesst die alten Zeilen stehen:
           niemand hat ihn ausgeloest, und eine Fehlermeldung ueber einen
           Abruf, den keiner wollte, waere schlimmer als ein etwas aelterer
           Stand. Der naechste Lauf versucht es wieder. */
        if (still) return;
        setFehler(f instanceof Error ? f.message : 'Unbekannter Fehler.');
      } finally {
        if (blinkschutz !== undefined) {
          clearTimeout(blinkschutz);
          blinkschutz = undefined;
        }
        if (!steuerung.signal.aborted) setLaedt(false);
      }
    })();

    return () => {
      steuerung.abort();
      if (blinkschutz !== undefined) clearTimeout(blinkschutz);
    };
  /* Der Stichtag steht in den Abhaengigkeiten, obwohl ihn der Klient aus
     einer Modulvariablen liest. Ohne ihn bliebe beim Umschalten jede schon
     geladene Liste stehen — mit dem Band „Ansicht vom 9. September" darueber
     und den Zahlen von heute darunter. */
  }, [pfad, mandant, runde, stichtag]);

  const erneut = useCallback(() => setRunde((r) => r + 1), []);

  /* Auf Schreibvorgaenge hoeren und neu laden.
     Nur, wenn die Sammlung im eigenen Pfad vorkommt — sonst zoege ein
     angelegter Klaerungspunkt jede Liste der Anwendung neu, und bei einer
     Ansicht mit fuenf Abrufen waeren das fuenf ueberfluessige Anfragen. */
  useEffect(() => {
    if (!pfad) return;
    const hoeren = (e: Event) => {
      const meldung = (e as CustomEvent<Bestandsmeldung>).detail;
      const sammlung = meldung?.sammlung;
      if (!sammlung || !pfad.includes(`/api/${sammlung}`)) return;
      /* Die letzte Meldung entscheidet: zwei kurz hintereinander ergeben
         ohnehin nur einen Abruf, weil der neue den alten abbricht. */
      stillNaechster.current = !!meldung.still;
      setRunde((r) => r + 1);
    };
    window.addEventListener(BESTAND_GEAENDERT, hoeren);
    return () => window.removeEventListener(BESTAND_GEAENDERT, hoeren);
  }, [pfad]);

  return { daten, laedt, fehler, erneut };
}

/* --------------------------------------------------------------- Uebersicht */

export interface Uebersicht {
  mandant: { code: string; name: string; personenbezug: boolean };
  kennzahlen: Record<string, number>;
  aufzaehlungen: Record<string, string[]>;
  letzterImport: { quelle: string; datenstand: string; version: string; zeitpunkt: string } | null;
}

export const useUebersicht = (mandant: string) => useAbruf<Uebersicht>('/api/uebersicht', mandant);

export interface Ich {
  name: string;
  kennung: string;
  /**
   * Die **Entra**-Anwendungsrollen aus dem Token: `Nutzer` oder
   * `Verwaltung`. Die grobe Auskunft.
   *
   * Hier stand bis zum 27.08.2026 „Anwendungsrollen des Kontos, z. B.
   * `iam-admin`" — das ist die Bedeutung, die der Entwicklungsserver
   * lieferte, und nicht die der Auslieferung. Wer sich darauf verlassen
   * hätte, hätte beim Kunden etwas anderes gesehen als lokal.
   */
  rollen: string[];
  /** Die Anwendungsrollen des Kontos aus `iam.Kontorolle`, z. B. `iam-admin`. */
  anwendungsrollen?: string[];
  darfVerwalten: boolean;
  /**
   * Schluessel des Kontos in `iam.Konto`. `null`, solange die Anmeldung keinem
   * Konto zugeordnet ist — dann gibt es auch keine Rechte.
   */
  kontoSk?: number | null;
  oeCode?: string | null;
  /**
   * Die aufgeloesten Rechte des Kontos.
   *
   * Bis zum 11.08.2026 kamen die Rechte aus der im Konto-Menue gewaehlten
   * Persona — einer Angabe des Browsers. Wer sie umstellte, aenderte damit
   * seine eigenen Rechte. Jetzt entscheidet die Schnittstelle.
   */
  rechte?: string[];
  /**
   * Ab welchem Tag die Datenbank einen Verlauf fuehrt (`JJJJ-MM-TT`), oder
   * `null`, wenn sie keinen fuehrt.
   *
   * Der Rueckblick reicht nicht weiter zurueck. Die Wahl in der Kopfzeile
   * bietet deshalb keine frueheren Tage an — sonst endete jeder Griff in
   * einer Absage.
   */
  verlaufAb?: string | null;
}

export const useIch = (mandant: string) => useAbruf<Ich>('/api/ich', mandant);

/* -------------------------------------------------------------------- Liste */

export interface Listenfrage {
  seite: number;
  groesse: number;
  sortierung?: string | undefined;
  richtung?: 'auf' | 'ab' | undefined;
  suche?: string | undefined;
  filter?: Record<string, string | string[] | undefined> | undefined;
  /**
   * Fasst die Liste serverseitig zusammen und blaettert ueber die Gruppen.
   *
   * Kein Filter, sondern ein Steuerwort — deshalb steht es hier und nicht in
   * `filter`, wo die Schnittstelle es zu Recht als unbekanntes Feld abwiese.
   */
  gruppierung?: string | undefined;
  /**
   * Schaltet die Abfrage ganz ab — es wird nichts angefordert.
   *
   * Gedacht fuer Sammlungen, die ein Mandant gar nicht fuehrt: eine Anfrage,
   * die absehbar mit 404 endet, soll erst gar nicht hinausgehen.
   */
  aus?: boolean | undefined;
}

/**
 * Entprellter Wert — fuer Eingabefelder, die bei jedem Zeichen abfragen.
 *
 * Ohne ihn schickt die globale Suche bei „Adobe" fuenf Anfragen ueber vier
 * Sammlungen, also zwanzig; vier davon interessieren.
 */
export function useEntprellt<T>(wert: T, ms = 250): T {
  const [spaet, setzeSpaet] = useState(wert);
  useEffect(() => {
    const uhr = setTimeout(() => setzeSpaet(wert), ms);
    return () => clearTimeout(uhr);
  }, [wert, ms]);
  return spaet;
}

export function useListe<T>(
  sammlung: string,
  mandant: string,
  frage: Listenfrage,
): Zustand<Seite<T>> {
  const pfad = frage.aus ? null :
    `/api/${sammlung}` +
    alsAbfrage({
      seite: frage.seite,
      groesse: frage.groesse,
      sortierung: frage.sortierung,
      richtung: frage.richtung,
      suche: frage.suche,
      gruppierung: frage.gruppierung,
      ...(frage.filter ?? {}),
    });
  return useAbruf<Seite<T>>(pfad, mandant);
}

/**
 * **Alle** Zeilen einer Liste — Seite um Seite, bis `gesamt` erreicht ist.
 *
 * Die Schnittstelle deckelt jede Seite bei `MAX_SEITENGROESSE` (200). Eine
 * Liste mit `groesse: 200` war deshalb bei jedem Bestand darueber still
 * abgeschnitten. Gemeldet am 30.09.2026: im Rolleneditor waren die Vorlagen
 * „nur bis R…" waehlbar — die HOWOGE fuehrt 321 Rollen, alphabetisch nach
 * der zweihundertsten war Schluss, und „Als Vorlage kopieren" an einer
 * spaeteren Rolle blieb dauerhaft bei „Die Rolle wird vorbereitet…".
 *
 * Gedacht fuer Auswahllisten und Ausfuhren, nicht fuer Tabellen: die
 * blaettern selbst. Eine Obergrenze von `MAX_SEITEN` Seiten schuetzt vor
 * einer Schleife, falls `gesamt` einmal nicht stimmt.
 */
const SEITE_ALLE = 200;
const MAX_SEITEN = 100;

export function useAlleSeiten<T>(
  sammlung: string,
  mandant: string,
  frage: Omit<Listenfrage, 'seite' | 'groesse'>,
): Zustand<Seite<T>> {
  const pfad = frage.aus ? null :
    `/api/${sammlung}` +
    alsAbfrage({
      seite: 1,
      groesse: SEITE_ALLE,
      sortierung: frage.sortierung,
      richtung: frage.richtung,
      suche: frage.suche,
      ...(frage.filter ?? {}),
    });
  return useAbruf<Seite<T>>(pfad, mandant, alleSeitenHolen);
}

/**
 * Der Lader hinter `useAlleSeiten` — auch ohne Haken nutzbar, etwa wenn ein
 * Editor nach dem Speichern den **jetzigen** Stand braucht und nicht den,
 * den ein Haken beim naechsten Zeichnen liefert. `erster` ist der Pfad mit
 * `seite=1` und `groesse=…`.
 */
export async function alleSeitenHolen<T>(
  erster: string,
  optionen: { mandant: string; token: string | undefined; signal?: AbortSignal | undefined },
): Promise<Seite<T>> {
  const eins = await holen<Seite<T>>(erster, optionen);
  const zeilen = [...eins.zeilen];
  for (let seite = 2; seite <= Math.min(eins.seiten, MAX_SEITEN); seite += 1) {
    if (optionen.signal?.aborted) break;
    const naechste = await holen<Seite<T>>(
      erster.replace(/([?&])seite=1(&|$)/, `$1seite=${seite}$2`), optionen);
    zeilen.push(...naechste.zeilen);
  }
  return { ...eins, zeilen, seite: 1, seiten: 1, groesse: zeilen.length };
}

/** Pfad fuer `alleSeitenHolen` — dieselbe Form, die `useAlleSeiten` baut. */
export function alleSeitenPfad(
  sammlung: string,
  frage: Omit<Listenfrage, 'seite' | 'groesse' | 'aus'>,
): string {
  return `/api/${sammlung}` + alsAbfrage({
    seite: 1,
    groesse: SEITE_ALLE,
    sortierung: frage.sortierung,
    richtung: frage.richtung,
    suche: frage.suche,
    ...(frage.filter ?? {}),
  });
}

/**
 * **Ein** Datensatz, gezielt geholt.
 *
 * Der Unterschied zu `useListe` ist der Grund, aus dem es diesen Haken gibt:
 * eine Liste liefert eine **Seite**, und die Schnittstelle deckelt sie bei
 * `MAX_SEITENGROESSE` (200). Wer einen Satz darin sucht, findet ihn nur,
 * wenn er auf die erste Seite passt.
 *
 * Genau daran hing der Rolleneditor bis zum 27.08.2026: er suchte die zu
 * bearbeitende Rolle in einer Liste mit `groesse: 200` — die der Server auf
 * 200 kuerzte — und der Kundenbestand fuehrt 321 Rollen. Eine Rolle, deren
 * Kennung alphabetisch hinter der zweihundertsten steht, war fuer den Editor
 * nicht vorhanden: er zeigte „Die Rolle wird vorbereitet…", dauerhaft. Der
 * Kunde hat es an einer frisch angelegten Rolle gemeldet.
 *
 * `daten` ist `null`, solange geladen wird **und** wenn es den Satz nicht
 * gibt; `fehler` unterscheidet beides. Ein 404 ist hier kein Ausfall, sondern
 * eine Auskunft — die Aufrufstelle muss sie geben.
 */
export function useSatz<T>(
  sammlung: string,
  mandant: string,
  schluessel: string | null,
): Zustand<T> {
  return useAbruf<T>(
    schluessel ? `/api/${sammlung}/${encodeURIComponent(schluessel)}` : null,
    mandant,
  );
}

/* ------------------------------------------------------------ Verzeichnisbaum */

export interface Baumknoten {
  pfad: string;
  tiefe: number;
  /** Assets unterhalb dieses Knotens, einschliesslich seiner selbst. */
  blaetter: number;
}

export interface Baum {
  assetTyp: string;
  knoten: Baumknoten[];
  gesamt: number;
}

/**
 * Der Verzeichnisbaum der Laufwerke — **serverseitig** gezaehlt.
 *
 * Die Assetansicht baute ihn bis zum 25.08.2026 im Browser aus hoechstens 200
 * geladenen Zeilen und zeigte davon 80 Knoten. Beim Kundenbestand sind es 459
 * Pfade: der Baum war ein Ausschnitt, der sich nicht als solcher zu erkennen
 * gab.
 */
export function useBaum(mandant: string, assetTyp: string, aus = false): Zustand<Baum> {
  return useAbruf<Baum>(
    aus ? null : `/api/assets/_baum${alsAbfrage({ assetTyp })}`,
    mandant,
  );
}

/* ---------------------------------------------------------------- Werteliste */

export interface Werteliste {
  feld: string;
  werte: { wert: string; zahl: number }[];
}

/**
 * Werte eines Feldes mit Zaehlern — traegt die Spaltenmenues der Tabellen.
 *
 * `feld` ist null, solange kein Spaltenmenue offen ist; dann wird nicht
 * abgerufen. Die Filter kommen ohne die Spaltenfilter selbst, sonst wuerde
 * eine getroffene Auswahl die uebrigen Werte aus der Liste werfen.
 */
export function useWerte(
  sammlung: string,
  mandant: string,
  feld: string | null,
  filter: Record<string, string | undefined>,
): Zustand<Werteliste> {
  const pfad = feld
    ? `/api/${sammlung}/_werte` + alsAbfrage({ feld, ...filter })
    : null;
  return useAbruf<Werteliste>(pfad, mandant);
}

/* -------------------------------------------------------------- Kreuztabelle */

export interface Kreuzzelle {
  zeile: string;
  spalte: string;
  anzahl: number;
  summe: number | null;
}

export interface Kreuztabelle {
  zeile: string;
  spalte: string;
  zellen: Kreuzzelle[];
}

/**
 * Zwei Felder gegeneinander gruppiert — traegt die Zugriffs-Heatmap.
 *
 * Die Aggregation laeuft in der Datenbank; die Oberflaeche bekommt nur die
 * belegten Zellen, nicht den Bestand.
 */
export function useKreuztabelle(
  sammlung: string,
  mandant: string,
  frage: {
    zeile: string;
    spalte: string;
    summe?: string | undefined;
    grenze?: number | undefined;
    filter?: Record<string, string | undefined> | undefined;
    /** Schaltet den Abruf ab — wie bei `useListe`. */
    aus?: boolean | undefined;
  },
): Zustand<Kreuztabelle> {
  const pfad = frage.aus ? null :
    `/api/${sammlung}/_kreuztabelle` +
    alsAbfrage({
      zeile: frage.zeile,
      spalte: frage.spalte,
      summe: frage.summe,
      grenze: frage.grenze,
      ...(frage.filter ?? {}),
    });
  return useAbruf<Kreuztabelle>(pfad, mandant);
}

/* ------------------------------------------------------------- Mandantenliste */

export interface Mandant {
  code: string;
  name: string;
  personenbezug: boolean;
}

export function useMandanten(): Zustand<Mandant[]> {
  // Ohne Mandantenkopf — diese Liste sagt ja erst, welche es gibt
  return useAbruf<Mandant[]>('/api/mandanten', '');
}
